/**
 * 全局配置
 */
import { ScriptGenre, ScriptTheme } from '../data/types';

/**
 * ===== 属性尺度常量（ATTR SCALE，批3-A；尺度扩充批 2026-09-28 拆双尺）=====
 * 设计稿与数据文件一律按 100 制书写，启动时由 GameData 对整张表做一次性预处理。
 * 本常量 = 「门槛尺子」倍率：属性 **min + max** + startRange / requirements / 事件 dc /
 *   骰子权重 / 评分除数 / 口碑门槛(咖位晋升线) / 商店属性加成 / UI 上限。
 *   ⚠️ min 于 2026-10-04（口碑可负批）补进本名单：历史上进表的 min 全是 0，乘不乘看不出来，
 *     直到口碑写成 -100（运行时 -500）——⛔ 乘就会得到「下界 -100 配上限 500」的错尺。
 * GROWTH_SCALE = 「成长侧」倍率（本批与门槛尺解耦）：attributeGrowth(杀青) /
 *   事件 effects+failEffects 的属性键 / 行程 attributeGain（课程）。
 * 本批定档=全案：门槛尺 3→5（属性上限 300→500、试镜门槛/事件 DC 同步抬 5/3），
 *   成长侧保持原绝对值 ×3 不变 ⇒ 属性追门槛的速度变成原来的 3/5，中后期试镜/骰子收紧。
 * ⛔ 两值只准填正整数（非整数会引入零头取整漂移）。
 * ⛔ 改门槛尺 = 老存档作废（存档按当时尺度存属性/门槛），需同步升 SAVE_VERSION。
 * 刻意【不随尺度缩放】的非比例项：体力 energy、名气 fame、金钱 money、粉丝、NPC 好感、剧本 baseScore 评分制。
 * ⚠️ 曾把「杀青口碑获取 (评分−6)×系数」也列入本名单（批3-A 有意收紧口碑线），实际收得过头：
 *   口碑上限与晋升线一起被门槛尺抬到 500/300/400/500，收入端一档没跟 ⇒ 满档 18 年只进账 217 点、
 *   Lv.8 线终生将将碰到。2026-10-03 单独把该系数 2→4 手工回填（⛔ 自动乘尺度，仍是一格可调的常数），
 *   读数与实测见 REPUTATION_SCORE_FACTOR 注释。
 */
export const ATTR_SCALE = 5;
/** 成长侧倍率（课程/杀青/事件属性增删走本值，与门槛尺 ATTR_SCALE 独立）。
 *  3→2（2026-10-31 茄子令）：治「属性太容易练满 ⇒ 满练后全部试镜自动过」——成长减速 =
 *  试镜门槛相对收紧 + 上限更难摸到，属性 max/角色 requirements 暂不动（分层抬要求的方案备而未用，
 *  看本格实测再定）。只乘表数据、玩家已练属性值原样读 ⇒ 老档兼容，⛔ 不需要升 SAVE_VERSION。 */
export const GROWTH_SCALE = 2;

export const GameConfig = {
    /** 开局年龄 */
    START_AGE: 22,
    /** 结束年龄 */
    END_AGE: 40,
    /** 每月周数 */
    WEEKS_PER_MONTH: 4,
    /** 每月行程数 */
    SCHEDULES_PER_MONTH: 4,

    /** 体力上限 */
    ENERGY_MAX: 100,
    /** 体力下限（归零时强制休息，不导致游戏结束） */
    ENERGY_MIN: 0,
    /** 归零时强制休息 */
    FORCE_REST_ON_ZERO_ENERGY: true,

    /** 咖位等级数 */
    RANK_MAX: 10,

    /**
     * **咖位名气门槛**（名气 → 咖位映射的唯一真源；下标＝咖位-1，Lv.1 恒为 0，必须严格递增）。
     * 读数口：`RankSystem.getRankFromFame() / getRankThreshold() / getPromotionStep() / getRankProgress() / getThresholds()`。
     *
     * 【2026-10-22 咖位曲线批·用户拍板】Lv.1~3 保持旧值（30/80 不动）；Lv.3 起改**等比 ×3.7**
     * （80 → 300 → 1100 → 4000 → 15000），让“小有名气→二线→一线→顶流”每段都拉开一个量级；
     * Lv.8/9/10 只 +3000 一级＝**名气退化为入场券**（顶流之后真正的门是口碑/作品/奖项，
     * 见 `RankSystem.RANK_PROMOTION_GATES`）。旧表问题：顶流只要 2200（第 5 年就摸到），
     * 而 8/9/10 突然跳 6000/12000/24000 ⇒ 张力全堆在尾段、且名气长期是唯一拦路虎。
     * 粉丝口径：粉丝锚 = 名气 × FANS_ANCHOR_K（2000 拍板值不动）⇒ 重排后顶流 30000 ≈ 6000 万粉、
     * Lv.10 36000 ≈ 7200 万粉（2026-10-31 满级线 24000→36000，茄子知情接受）。
     * 本表 NPC 侧共用（`NormalNpcSystem` 由 fame 反算 rank）⇒ 一线以上 NPC 需要更多作品积累，
     * 顶级角色更多由低咖 NPC 顶上去＝派角②「向下顺延=晋升通道」的设计内行为。
     */
    RANK_FAME_THRESHOLDS: [
        0,      // Lv.1 无人在意
        30,     // Lv.2 稍有人知
        80,     // Lv.3 圈内眼熟
        300,    // Lv.4 小有名气
        1100,   // Lv.5 三线艺人（2026-10-31 咖位重排：三线插入）
        4000,   // Lv.6 二线艺人
        15000,  // Lv.7 一线艺人
        30000,  // Lv.8 顶流艺人（重排：一线→顶流 ×2（2026-10-31 茄子定「乘两倍差不多」）；之后每级只 +3000）
        33000,  // Lv.9 国民女神（入场券：一部半戏即穿，真门=口碑/作品/奖）
        36000,  // Lv.10 国际巨星（原 Lv.9 影坛天后已并入；满级线 24000→36000，茄子知情接受）
    ] as number[],

    /** 通告签约上限 */
    MAX_PENDING_SCRIPTS: 3,

    /** 试镜能耗（试镜周执行时一次性扣除，一周试多个剧本不额外扣）。2026-10-08 起与全表统一 25/周 */
    AUDITION_ENERGY_COST: 25,

    /**
     * 特训授能力 tag 的累计完成次数（特训重做批 2026-10-30）：同一特训练满本次数 →
     * addTag 对应 TAG_SKILL_*（唯一获取途径，结算落 ScheduleSystem.settleSpecialTraining）。
     */
    SPECIAL_TRAINING_TAG_AT: 10,

    /**
     * 剧级档期宽放系数（2026-10-27 滚动排期批·茄子定）：玩家违约线 = 一番角色拍摄周期 × 本值进整月。
     * NPC 拍摄走制作窗（scriptShootMonths），本系数只约束玩家「拖过档期=违约」的死线长短。
     */
    SCHEDULE_GRACE_MULTIPLIER: 1.5,

    /** 新手首单专属剧本（2026-10-27 拍板「另加个新剧本」）：不进排期表、不进随机池，开局直签递它 */
    NEWBIE_SCRIPT_ID: 'SCRIPT_T01',

    /** 晋升二线送本专属剧本（2026-10-27 茄子令「升到 Lv.5 固定送一个角色，专门写个剧本」）：
     *  同新手剧口径——不进排期表/随机池，首次跨入 Lv.5 时由 DirectSignSystem.issuePromotionOffer 直签递它 */
    PROMOTION_SCRIPT_ID: 'SCRIPT_T05',

    /** 违约金 = 报酬 × 该系数（主动解约 / 档期违约共用）；批4 由 4 降为 2 */
    CONTRACT_PENALTY_MULTIPLIER: 2,
    /** 角色等级 ≤ 该值（龙套/特约）免违约金：小角色剧组随时能换人，不演有的是人演（批4 定稿，贴现实轻合约行规） */
    PENALTY_FREE_MAX_ROLE_LEVEL: 2,
    /**
     * 片酬基准表（下标 = 角色等级-1，Lv.1~10）：docs/剧本/导入台账.md §2 的代码镜像。
     * **表管结构、PAY_SCALE 管量级**（2026-10-30 结构改版，茄子拍板）：本表=×1 相对口径，
     * 结算统一 ×PAY_SCALE；倍率链前陡后平（×6/×5/×4/×4/×3/×2.8/×1.8/×1.56/×1.43）——
     * 中段跃迁感（升一档跳一截）、顶流（终值 600 万）后缓增不再翻倍。
     * 调表 = 改这一个数组 + 升 SAVE_VERSION（结算读当前表，在途合同按新表结算）。
     * Lv.10 立档（2026-10-30）：国际巨星档，特殊剧本缺省兜底按本档。
     */
    PAY_BY_ROLE_LEVEL: [500, 3000, 15000, 60000, 240000, 720000, 2000000, 3600000, 5600000, 8000000] as number[],
    /**
     * 钱域全局量级倍率（2026-10-30 茄子拍板）：片酬（rewardOf）/通稿/公关/代言报价四处统一乘。
     * 兼职**有意不乘**（现实时薪锚，「拍戏 vs 打工」差距拉开是设计意图）。
     * ⛔ 任何基准表预先乘本值 = 双重乘法（表管结构、SCALE 管量级，别越界）。
     */
    PAY_SCALE: 3,
    /** 竞角失败「带资进组」价格 = 报酬 × 该系数（随剧本咖位逐级递增，后期平衡可调） */
    BUYIN_MULTIPLIER: 4,
    /** 通告拍摄周体力消耗（与普通工作一致；体力不足非排程约束，执行期兜底改在家休息）。2026-10-08 起与全表统一 25/周 */
    CONTRACT_ENERGY_COST: 25,

    // ===== 经纪人工作台（2026-10-27 拍板）：买通稿=钱买名气带反噬；危机公关=钱削负口碑 =====
    // 红线：正口碑只能从戏里来（RankSystem 晋升三门口径），通稿只动名气；公关只把负分清到 0，
    // 两者的实现都不产生任何正口碑（桩测有红线断言）。
    /** 买通稿价格表（下标 = 玩家咖位-1，Lv.1~10）：对齐片酬表的量纲递增 */
    PROMO_PRICE_BY_RANK: [1000, 2500, 6000, 15000, 35000, 80000, 180000, 300000, 450000, 600000] as number[],
    /** 通稿名气增量 = max(10, 当前名气 × 本值)（比例式，任何阶段都有感；免费营业帖=2%且封顶，通稿花真钱故不封顶） */
    PROMO_FAME_RATE: 0.06,
    /** 买通稿每月次数上限（月界重置） */
    PROMO_MONTHLY_LIMIT: 2,
    /** 反噬（被扒）基础概率；每买一次 +每买递增，封顶反噬概率上限——越买越容易被扒，自刹车 */
    PROMO_BACKFIRE_BASE: 0.12,
    PROMO_BACKFIRE_PER_BUY: 0.04,
    PROMO_BACKFIRE_CAP: 0.4,
    /** 被扒口碑惩罚 = -(基础 + 咖位 × 每级)；Lv.5=−40、Lv.8=−55（口碑区间 -500~500，疼不毁档） */
    PROMO_BACKFIRE_REP_BASE: 15,
    PROMO_BACKFIRE_REP_PER_RANK: 5,
    /** 危机公关单点价表（下标 = 玩家咖位-1）：总价 = |负口碑余额| × 单点价，一键清到 0；无月限（价格即闸） */
    PR_PRICE_PER_POINT_BY_RANK: [100, 150, 200, 300, 450, 700, 1000, 1400, 1800, 2200] as number[],

    // ===== 商务代言（2026-10-30 拍板，设计 canonical = docs/玩法设计.md §六「商务代言」）=====
    // 红线：商务只给钱+名气/粉丝，不产生正口碑（口碑只能靠演好戏拿，RankSystem 三门口径）；
    // 微商签约掉口碑是负向（走 addAttribute 总闸，黑料缠身 ×1.5 自动生效）。
    // 报价通式 = 基准价 × 品牌档预算系数 × 名气系数 × PAY_SCALE（⛔ 任何表预乘 SCALE = 双重乘法）。
    /**
     * 月掷基础率（下标 = 玩家咖位-1）：Lv.4 开锁（微商先递）10% 起**随咖位递增至 40%**——
     * 2026-10-30 茄子实测「国民女神后代言变稀」整改：旧表 Lv.5~10 恒 15%（高咖位频率与 Lv.5 同）
     * 背离「咖位越高商务越多」，对齐直签递本阶梯（10%→40% 递增）的形状。爆款/T2 另乘。
     */
    ENDORSE_ROLL_RATE_BY_RANK: [0, 0, 0, 0.10, 0.12, 0.15, 0.20, 0.26, 0.32, 0.40] as number[],
    /** 爆款加成：最近完结作品热度曾过爆线 → 完结月起 N 月内月掷 ×本值 */
    ENDORSE_BOOM_MULT: 2,
    ENDORSE_BOOM_MONTHS: 2,
    /** 放弃品牌拉黑窗口（月）：放弃后 N 个月内不重复递，窗口过自动解禁（2026-10-30 茄子令「五年内」） */
    ENDORSE_REJECT_BLACKOUT_MONTHS: 60,
    /** 解约后再递约冷却（月）：品牌方终止/玩家主动解约/挚友违约=关系破裂，24 个月内同品牌不再递（2026-11-03 茄子令） */
    ENDORSE_TERMINATE_COOLDOWN_MONTHS: 24,
    /** 约满自然到期/放弃续约后再递约冷却（月）：和平分手保留「后续再合作」叙事，但至少隔一年（2026-11-03 茄子令） */
    ENDORSE_EXPIRED_COOLDOWN_MONTHS: 12,
    /** 黑红联动：口碑 T2 及以下（≤-101）或挂黑料缠身 → 月掷 ×本值（流量型品牌凑话题；国民/高奢此档不递约） */
    ENDORSE_T2_RATE_MULT: 1.5,
    /** 品牌档预算系数：报价 = 基准 × 本系数 × 名气系数 × PAY_SCALE（虚拟 key=BrandTier） */
    ENDORSE_TIER_BUDGET: { ws: 1.5, cl: 0.6, xf: 0.9, gm: 1.0, gs: 1.2 } as Record<string, number>,
    /** 品牌档要求咖位线（递约带下沿）；解约判定改读同档对应的 RANK_FAME_THRESHOLDS 名气门槛（2026-11-02 茄子令） */
    ENDORSE_TIER_MIN_RANK: { ws: 4, cl: 5, xf: 5, gm: 6, gs: 7 } as Record<string, number>,
    /** 代言基准年费表（下标 = 玩家咖位-1，×1 相对口径 = 片酬基准×1.25 逐档锁定；挚友单次/大使另见比例）。⛔ 预乘 SCALE */
    ENDORSE_PAY_BY_RANK: [0, 0, 0, 75000, 300000, 900000, 2500000, 4500000, 7000000, 10000000] as number[],
    /** 名气系数带：clamp(当前名气 ÷ 本咖位名气门槛, 下限~上限)；门槛≤0 防护=1 */
    ENDORSE_FAME_MULT_MIN: 0.5,
    ENDORSE_FAME_MULT_MAX: 2.0,
    /**
     * 考察期晋升概率（A 版，2026-11-03 茄子令）：名气越大越容易升——
     * `基础 10% + (名气系数 − 1) × 40%`，名气系数同报价口径（名气÷本咖位门槛，0.5~2.0），clamp 10%~50%。
     * 仅高奢品牌大使约、季末判定（3/6/9/12 月），失败无惩罚。
     */
    ENDORSE_PROMOTE_BASE_PCT: 0.10,
    ENDORSE_PROMOTE_FAME_BONUS: 0.40,
    ENDORSE_PROMOTE_PCT_MIN: 0.10,
    ENDORSE_PROMOTE_PCT_MAX: 0.50,
    /**
     * 高奢直接递品牌代言人的概率（按咖位下标 rank−1，2026-11-03 茄子令）：Lv1~Lv7 恒 0、Lv8=10%、Lv9=25%、Lv10=40%。
     * 只在高奢档掷中后判定，未中仍递品牌大使；折算月概率（掷率×高奢档权重×本概率）约 Lv8 0.55%、Lv9 2.40%、Lv10 6.56%。
     */
    ENDORSE_GS_SPOKESMAN_PCT_BY_RANK: [0, 0, 0, 0, 0, 0, 0, 0.10, 0.25, 0.40] as number[],
    /** 品牌解约宽限：名气连续 N 月低于品牌要求才解约（口碑档/黑料是立即解约，仅高奢·国民适用） */
    ENDORSE_LOWRANK_GRACE_MONTHS: 6,
    /** 违约金 = 年费（×PAY_SCALE 后）× 本值；品牌解约与玩家主动解约同（已付头款不追回=游戏从宽；全额扣，存款不足可扣成负数=欠债） */
    ENDORSE_PENALTY_MULT: 1,
    /** 微商签约掉口碑 = -(基础 + 咖位×每级)（Lv.4=−22、Lv.10=−40：比数值事件重一档、低于通稿被扒） */
    ENDORSE_WS_REP_BASE: 10,
    ENDORSE_WS_REP_PER_RANK: 3,
    /** 官宣名气（一次性挂签约；粉丝锚=fame×K 月末自动跟随不单列） */
    ENDORSE_ANNOUNCE_FAME: { friend: 3, ambassador: 10, spokesman: 20 } as Record<string, number>,
    /**
     * 递约带软分布（下标 = 玩家咖位-1，权重 [微商ws, 潮流cl, 新消费xf, 国民gm, 高奢gs]，roll 后归一化）。
     * 主带集中+邻带小概率；高奢 Lv.4~6 的小权重=「押潜力股」首约（挚友档），Lv.7+ 只递大使/代言人。
     */
    ENDORSE_TIER_WEIGHTS: [
        [0, 0, 0, 0, 0],            // Lv.1 未开锁
        [0, 0, 0, 0, 0],            // Lv.2
        [0, 0, 0, 0, 0],            // Lv.3
        [0.82, 0.08, 0.05, 0.03, 0.02],  // Lv.4 微商主带
        [0.40, 0.25, 0.20, 0.10, 0.05],  // Lv.5（2026-10-30 茄子令：前三档整体上调）
        [0.18, 0.26, 0.30, 0.18, 0.08],  // Lv.6
        [0.08, 0.22, 0.32, 0.26, 0.12],  // Lv.7
        [0.04, 0.14, 0.28, 0.33, 0.21],  // Lv.8（2026-10-30 茄子令：微商递约带延展到 Lv.9）
        [0.02, 0.08, 0.22, 0.38, 0.30],  // Lv.9（微商 2% 小概率——顶流接微商是掉价事件，稀有但存在）
        [0, 0.04, 0.15, 0.40, 0.41],     // Lv.10 高奢主带
    ] as number[][],

    // ===== 宣传特访（2026-10-31 批；canonical = docs/玩法设计.md §综艺「常驻宣传综艺」）=====
    // 一番杀青**掷骰**递约（2026-10-31 茄子令「必得不合理」；同日多轮定稿=题材分流+形态资格池+同节目冷却）。
    // 掷中后从**资格池**随机挑一种宣传形态（综艺/路演/专访，未被冷却锁的）。
    // 收益全是**终值**（茄子拍板口径）——⛔ 不乘 PAY_SCALE；路演/专访不结钱（现实宣发行程没有通告费）。
    /** formId → 收益终值与显示标签；SCRIPT_* 从 SCRIPT_MAP 取节目名，非剧本形态用 label */
    VARIETY_PROMO: {
        SCRIPT_170: { fee: 200000, heat: 10, fame: 5, label: '' },             // 《周末openmic》游戏类·粉丝向低档
        SCRIPT_171: { fee: 450000, heat: 15, fame: 8, label: '' },             // 《大驾光临》访谈类·收一切
        SCRIPT_172: { fee: 900000, heat: 20, fame: 12, label: '' },            // 《星动之夜》舞台类·粉丝向高档
        SCRIPT_174: { fee: 150000, heat: 10, fame: 5, label: '《追更营业中》' }, // 平台剧综联动·仅网剧
        PROMO_ROADSHOW: { fee: 0, heat: 25, fame: 5, label: '路演' },           // 映前见面会·仅电影·给作品加热最直接
        PROMO_INTERVIEW: { fee: 0, heat: 5, fame: 15, label: '媒体专访' },       // 深度访谈·仅偏正剧向·立人设
    } as Record<string, { fee: number; heat: number; fame: number; label: string }>,
    /**
     * 题材分流（2026-10-31 茄子定，同日删喜剧）：剧命中**粉丝向题材**名单 → 咖位切档
     * （≤FAN_CUT_RANK 开麦 / 之上星动）；未命中（悬疑/犯罪/都市/生活/职业/正剧…）为**偏正剧向**。
     * ⛔ 不与节目自身 themes 求交（那是节目类型不是嘉宾画像，第一版踩过）。
     */
    VARIETY_PROMO_FAN_THEMES: [
        ScriptTheme.Romance, ScriptTheme.Idol, ScriptTheme.Xianxia,
        ScriptTheme.Campus, ScriptTheme.Music, ScriptTheme.Fantasy,
    ] as string[],
    /** 粉丝向池内咖位切档：≤本值 → 开麦，之上 → 星动（星动=头部排面，重排后顶流 Lv.8 起才进） */
    VARIETY_PROMO_FAN_CUT_RANK: 7,
    /** 正剧负向（2026-10-31 茄子令「正剧乘 0.5 就行，权谋宫廷不需要」）：剧带正剧题材 → 概率 ×0.5 */
    VARIETY_PROMO_SERIOUS_THEMES: [ScriptTheme.SeriousDrama] as string[],
    VARIETY_PROMO_SERIOUS_MULT: 0.5,
    /** 题材硬闸（2026-10-31 茄子令「战争谍战历史直接不上综艺」）：剧带任一 → 概率恒 0，整个宣传池不进 */
    VARIETY_PROMO_BAN_THEMES: [
        ScriptTheme.War, ScriptTheme.Spy, ScriptTheme.History,
    ] as string[],
    /**
     * 递约率 = 基础 × 质量 × 正剧负向，clamp 上限；质量按杀青时剧的 baseScore（头部剧才有宣发预算）。
     * 基础率 20%（2026-10-31 茄子令降频：30%→20%，配合同节目冷却）。
     */
    VARIETY_PROMO_BASE_PCT: 0.2,
    VARIETY_PROMO_QUALITY_LINE: 7,
    VARIETY_PROMO_QUALITY_MULT: 1.5,
    VARIETY_PROMO_QUALITY_TOP_LINE: 8,
    VARIETY_PROMO_QUALITY_TOP_MULT: 2,
    VARIETY_PROMO_PCT_CAP: 0.9,
    /**
     * 同节目冷却（2026-10-31 茄子令「现实不会一年上同一个综艺两次」）：上过（**录制月结**盖章）后
     * N 个月内该形态不再递；放弃邀约不算上过、不进冷却；冷却中=该形态从资格池剔除（池空=本部错过）。
     * 无全局冷却——不同形态之间不互相锁（跑通告本来就是多台跑）。
     */
    VARIETY_PROMO_SHOW_COOLDOWN_MONTHS: 12,

    /** 实际评分「罚项」：Σ(不达标差值) 除以该系数（每 25 点属性差 ≈ −1 分，再乘番位系数）；随尺度缩放保持换算比例不变。达标侧不走本项，见 SCORE_BONUS_* */
    SCORE_ATTR_DIVISOR: 25 * ATTR_SCALE,
    /**
     * 实际评分「奖励项」（2026-09-29 评分通胀修·方案B 拍板）：属性超出要求的部分不再线性加分
     * （旧公式中后期满配 86% 作品满分、烂本都能冲 9 分），改为按超额比例给小额奖励：
     *   每条要求 bonus += SCORE_BONUS_PER_REQ × min(超额比例 ÷ SCORE_BONUS_SAT_RATIO, 1)，合计封顶 SCORE_BONUS_TOTAL_CAP，再乘番位系数。
     * 饱和点 0.25 = 属性上限(100人值) 对全库最高要求(80人值) 的真实最大超额 ⇒ 满配演顶级角色才拿满档，碾压低级角色不多给。
     * 效果：剧本质量（baseScore）主导评分，属性只锦上添花有限幅度——「演技救不了烂剧本」成立。
     *
     * 【调参旋钮 2026-09-29 茄子拍板预留后续修改】空跑分布锚点（178 本 × 一~三番位）：
     * 后期均值 7.4 / 9.9 率 0%；满配均值 7.7 / 9.9 率 3%（9 分神本才摸得到顶）；烂本(<5)满配均值 4.7（永远烂片）。
     * - 觉得 9.9 太稀有 → 提 SCORE_BONUS_PER_REQ（0.35~0.4，满配神本更稳上顶）
     * - 觉得烂本被抬太多 → 降 SCORE_BONUS_TOTAL_CAP（≤1.0 烂本锁死 5 分以下）
     * - 两个旋钮都只动「达标侧」，不达标罚项（硬演扑街）不受影响
     */
    SCORE_BONUS_PER_REQ: 0.3,
    SCORE_BONUS_SAT_RATIO: 0.25,
    SCORE_BONUS_TOTAL_CAP: 1.2,
    /** 口碑影响 = (实际评分 − 该中线) × 系数（6 分为界，好片涨口碑、烂片掉口碑） */
    SCORE_REPUTATION_MIDLINE: 6,
    /**
     * 口碑影响系数：一部戏完结的口碑变化 = round((实际评分 − 中线) × 本值）。
     * 2 → 4（2026-10-03 口碑收入曲线批，用户拍板档位）：本值是 v13「属性尺度扩充」的漏乘项——
     *   口碑上限与 Lv.8/9/10 晋升线一起乘了 ATTR_SCALE=5 抬到 500/300/400/500，收入端一档没跟，
     *   于是满档玩家 18 年 72 部戏只进账 217 点（终局 311~315，Lv.8 线 Y18 才勉强碰到、Lv.9 终生摸不到），
     *   与名气那次同源、方向相反（名气=涨太快，口碑=涨不动）。
     * 实测（改后复跑，属性 400/500、每月满帖、⛔ 桩覆盖；分析脚本已清理，git 可查）：
     *   满档（4 部/年）剧完结 18 年进账 437 点＝口碑来源 96%，300 线落第 14 年、400 线第 16 年、
     *   500 线第 18 年（钳顶），终局名气随之从 20,357 抬到 27,194（口碑门放开 ⇒ 咖位重新吃得到名气台阶）；
     *   休闲（2 部/年）终局 281 ⇒ 终生过⛔ 300 线、卡 Lv.7 ＝「升不了 Lv.8 是线路不是缺陷」（§3.4r）。
     * ⚠️ 端点读数：9.9 分 +16、7.0 分 +4、6.0 分 0、5.0 分 −4、2.1 分 −16。
     *   口碑域 = -500~500（2026-10-04 起⛔ 再钳在 0，见 attributes.ts 的 reputation.min；其余七维仍 0 起），
     *   一部烂戏从此能真把口碑砸穿 0——这是「资源咖演砸被全网骂」那类事件的承载面（事件条目另批）。
     */
    REPUTATION_SCORE_FACTOR: 4,
    /** 实际评分上下限（2.1~9.9，豆瓣口径——两端都不出现整数极值：9.9 即封神、2.1 即谷底） */
    SCORE_MIN: 2.1,
    SCORE_MAX: 9.9,

    /** 事件判定骰子每点权重（100 制下 =10，随尺度缩放：判定式 floor(属性/2)+D6×本值 ≥ DC） */
    DICE_FACE_WEIGHT: 10 * ATTR_SCALE,
    /** 竞角胜率：每超门槛 1 点加成（100 制 =2，随尺度反比缩放保持"同超额比例→同胜率"不变） */
    WINRATE_BONUS_PER_POINT: 2 / ATTR_SCALE,

    /**
     * 统一番位系数（名气与评分共用一套）：
     *  - 名气加成 = 剧本热度(heat) × 系数（四舍五入取整）
     *  - 评分属性项 = 系数 × Σ(主角属性−角色要求)/25，但仅三番及以前参与——
     *    四番及以后（含六番+）角色属性对评分不构成影响（评分为纯 baseScore），系数只用于名气
     *  - 一番 ×1.0 / 二番 ×0.7 / 三番 ×0.5 / 四番 ×0.2 / 五番 ×0.1；无番位（综艺等）×0.5
     *  - 六番及以后名气按五番档（×0.1）处理
     */
    FAME_BILLING_FACTORS: { 1: 1.0, 2: 0.7, 3: 0.5, 4: 0.2, 5: 0.1 } as Record<number, number>,

    // ===== 名气主粮=作品 批（2026-10-03 用户裁定；实施记录=docs/实现说明.md §3.4q）=====
    /** 全库剧本热度中位（旧「台阶份额制」的剧质分母；现仅作热度读数的参照值，公式改用 FAME_HEAT_REF=100） */
    HEAT_MIDPOINT: 50,
    /**
     * **作品名气收益**（2026-10-22 咖位曲线批·用户拍板；2026-10-23 长线定标修·H 案，用户拍板靶子＝中等配置线）：
     *   gain = K × (heat ÷ HEAT_REF)^HEAT_EXP × 番位系数^BILLING_EXP × 咖位乘子 × 名气因子 × 软上限
     *   - 咖位乘子 = √(max(Lv.2 门槛, 本级名气门槛) ÷ Lv.2 门槛)　（`RankSystem.getRankFameMult`，由门槛表现算）
     *   - 名气因子 = 1 + COEF × log₂(1 + min(名气, 顶流线) ÷ 顶流线)，封顶 1 + COEF 倍
     *   - 顶流线 = Lv.7 门槛（`RankSystem.getToplineFameRef()` 同源现算，⛔ 不写死数值——写死即门槛表的漏更项）
     *   - 软上限锚 = 满级线 Lv.10 门槛（`RankSystem.getMaxFameThreshold()`）＝纯时间刹车，与顶流线**双锚分离**
     * 【2026-10-23 H 案改了什么、为什么】K 280→**400**、COEF 0.8→0.4、乘子读「下一级」→读「本级」、
     *   软上限锚 24000→顶流线再回满级线、指数 3→**5**。用户实测第 6 年 3 万+/7000 万粉，三方复核根因三条叠加：
     *   ① 尾段门档与乘子脱节——门槛 Lv.7→10 每级只 +3000，而乘子按「下一级门槛」开方还在跟涨
     *     （Lv.6=22.4、Lv.10=28.3）⇒ 一部戏 ≈6000~11000 点，Lv.7→8 台阶只 3000 ⇒「一部穿一级」是公式必然；
     *   ② 名气因子与软上限锚同一条线 ⇒ 名气冲过 15000 顶流线后到 24000 之间只加速不刹车，
     *     第 6 年 3 万正是这段；③ 粉丝锚=名气×2000 是放大镜，治名气即治粉丝（用户拍板不另批）。
     *   乘子改读**本级门槛**＝尾段收入不再跟涨（顶流后名气退化为入场券的原意落地）；floor 统一式
     *   `max(T[1], T[rank-1])` 让 Lv.1~2 自然=1，⛔ 写分支（Lv.1 门槛是 0，裸读会让新人乘子=0.18）。
     * 【K 与软上限指数是一对联动旋钮】用户拍板「靶子 1.68 万是**中等配置档位**的标准」后，
     *   K 与指数一起重定：K=115/指数 1.5 那版是按**顶配线**定的靶（中等线会被顶到 2.8 万、超靶 40%）；
     *   现 K=400/指数 5 按中等线定靶：中等线（属性 60）12 局中位终局 **17249**＝满级线 70~72%、咖位 Lv.7；
     *   顶配线（属性 95）43766＝182%（肝满线的上限档，刹车把它从 5 万+ 收到 4.4 万，咖位仍被口碑 300
     *   门钉在 Lv.8 ⇒「顶流后名气只是入场券」成立）；指数同时收窄两条线离散度（中等线 P25~P75
     *   1352~10874 → 3410~18262）。**读数以 `docs/剧本/导入台账.md` §7.2 为准**（本处不复述数值）。
     *   早期定标读数（旧尺作废）：常规 2.5 部/年 Lv.4@Y3、Lv.6@Y7、Y6 名气 3220/644 万粉、Y18 终局 3.4 万；
     *   名气来源占比 作品 92.8% / 奖项 2.1% / 事件行程 2.6% / 微博 2.5%。
     * 前代口径的历史教训保留：旧「台阶 × 份额」制作废的理由（Lv.7 台阶比 Lv.6 小 ⇒ 顶流拍同一部戏反而
     *   便宜 3.7 倍，倒挂）今天仍成立，所以乘子必须是门槛派生的单调式；但**开方读「下一级」是 10-22 那次
     *   只解了倒挂、没解尾段空转**——本级读法两者都满足。两条纪律照旧：咖位/名气取**杀青当时**快照、
     *   播完前⛔ 再变；热度只读**剧本底数**（砸剧宣/客串⛔ 抬滴灌）。
     * 改名气量纲前必读 docs/剧本/导入台账.md 的「全库同步点清单」（七条），漏一条＝第四次同源事故。
     */
    FAME_INCOME_K: 140,
    /** 热度口径分母（热度 0~100 归一；>100 仍继续增长但增速放缓） */
    FAME_HEAT_REF: 100,
    FAME_HEAT_EXP: 0.8,
    /** 番位系数指数（1.0＝线性，越大越惩罚低番位） */
    FAME_BILLING_EXP: 1.2,
    /** 名气因子系数：1 + 本值 × log₂(1 + min(名气, 顶流线) ÷ 顶流线)；顶流线以上封顶 1+本值 倍（0.8→0.4=复利助攻砍半） */
    FAME_FAME_FACTOR_COEF: 0.4,
    /**
     * 名气软上限指数（2026-10-22 长跑实测后加；2026-10-23 H 案重定标）：名气 > 满级线后
     * 收益 × (满级线 ÷ 名气)^本值。指数 **5**（2026-10-23 用户拍板靶子改挂中等配置线后由数值定档）。
     * 【锚点为什么从顶流线(15000) 回满级线(24000)】两条线语义本来不同，混用才是倒挂的根因：
     *  - **名气因子的钳点＝顶流线**（「顶流之后看口碑/作品/奖项」的复利闸门，保留在 `getToplineFameRef()`）；
     *  - **软上限＝纯时间刹车**（防名气膨胀到亿级粉丝），它一锚在顶流线，玩家一过 15000 就 (15000/fame)^n
     *    衰减，衰减速度盖过乘子的微涨 ⇒ 同一部戏 Lv.8 收益 < Lv.7（=「顶流拍同一部戏比一线低」，
     *    10-22 那次用户当场指出、我们写进设计约束「结构上不可能倒挂」的形态换了入口回来）。
     *  锚回满级线 ⇒ fame ≤ 24000 区间刹车恒 =1、乘子单调 ⇒ 「同一部戏高咖位收益 ≥ 低咖位」这条约束
     *  保持原样有效（用户 2026-10-23 裁定「尽量避免倒挂，这样不合理」，⛔ 不接受「接受倒挂并改约束」这条路）。
     * 【指数为什么是 5 而不是 3】指数决定「过满级线后还涨多快」，它与 K 是**一对联动旋钮**：
     *  - K=115/指数 1.5 那版是按**顶配线**定的靶 ⇒ 中等线被顶到 2.8 万（超靶 40%）；
     *  - K=400/指数 5 这版按**中等配置线**定靶（用户拍板「靶子 1.68 万是中等配置档位的标准」）：
     *    中等线 12 局中位 17249＝满级线 70~72%、终局 Lv.7；顶配线 43766＝182%（肝满线的上限档，
     *    刹车把它从 5 万+ 收到 4.4 万，咖位仍被口碑 300 门钉在 Lv.8 ⇒「顶流后名气只是入场券」成立）；
     *  - 指数还同时收窄两条线的离散度（中等线 P25~P75 从 1352~10874 收到 3410~18262）——
     *    刹车越陡，K 抬高带来的方差放大越小。
     *  满级线之上的名气只可能来自特殊剧本/开局咖位购买/老档增益，那正是这道刹车要拦的对象
     *  （用户那份 3 万档正落这段 ⇒ 刹车仍管得住）。
     *  0 = 关闭软上限（公式原口径「名气无上限」）。
     * 【2026-10-30 指数 5→10·茄子实测后期名气仍溢出】依据：braked 相位（fame>满级线）月增益 ∝
     *  (满级线/fame)^指数，以 K=140 会玩线基准（台账 §7.2 终局 38877）反推——指数 7 只砍
     *  braked 相位累计 ~60%（终局预测 ~3.0 万，治不了「还是溢出」）；指数 10 让增益在满级线
     *  之上一个量级内快速趋零，终局预测 ~2.5~2.6 万 ≈ 满级线附近，粉丝锚（×2000×charisma）
     *  回落 ~5000 万 = 回到 Lv.10=4800 万粉的既定数值区间。终局 Lv.8 靶不受影响（卡点在口碑门，
     *  名气 18000 线在刹车区之前）。注意：满级线之上残余进账=特殊剧本/开局咖位等免刹来源，刹车只管作品。
     */
    FAME_SOFT_CAP_EXP: 10,
    /**
     * 营业/公益名气封顶的「本级收入台阶」基数：basis = 本值 × 咖位乘子（Lv.1 = 30，与旧 Lv.1 台阶同量级）。
     * 2026-10-22 起这两条不再读 `getPromotionStep()`：门槛尾段是入场券（只 +3000），
     * 读台阶会让顶流的发帖收益反而低于一线（与作品收益同源的倒挂）。
     */
    FAME_INCOME_BASIS_BASE: 30,
    /**
     * 顶流后的「主演高分作品」门（2026-10-22 咖位曲线批）：一番/二番 且 实际评分 ≥ 本值 的条数，
     * 门槛在 `RankSystem.RANK_WORK_GATES`（Lv.8/9/10 = 2/4/6 部）。
     */
    RANK_BEST_WORK_MIN_SCORE: 8.0,
    /** 算作「主演」的最大番位（含）：1 番、2 番 */
    RANK_BEST_WORK_MAX_BILLING: 2,
    /** 无番位角色（综艺嘉宾等）的系数 */
    FAME_FACTOR_NO_BILLING: 0.5,

    /**
     * 剧本 heat 公式：heat = round(baseScore × 类型系数[genre] × 题材系数)
     * 题材系数 = 所有已标题材系数的算术平均值
     * 单题材时退化为主题材系数本身。
     * 详细规则见 docs/剧本/导入台账.md §1。
     */
    /** 类型→heat 系数 */
    GENRE_HEAT_FACTORS: {
        short: 3,
        web: 6,
        variety: 5,
        tv: 7,
        movie: 6,
        other: 5,
    } as Record<ScriptGenre, number>,
    /** 题材标签→heat 系数 */
    THEME_FACTORS: {
        [ScriptTheme.Ancient]: 1.2,
        [ScriptTheme.War]: 0.9,
        [ScriptTheme.Spy]: 0.9,
        [ScriptTheme.Romance]: 1.2,
        [ScriptTheme.Idol]: 1.3,
        [ScriptTheme.Mystery]: 1.2,
                [ScriptTheme.Urban]: 1.1,
                [ScriptTheme.Xianxia]: 1.3,
        [ScriptTheme.Fantasy]: 1.0,
        [ScriptTheme.History]: 0.9,
        [ScriptTheme.Period]: 0.8,
        [ScriptTheme.LifeStyle]: 1.0,
        [ScriptTheme.Crime]: 1.1,
        [ScriptTheme.Court]: 1.2,
        [ScriptTheme.MartialArts]: 1.0,
        [ScriptTheme.Action]: 1.0,
        [ScriptTheme.Horror]: 0.7,
        [ScriptTheme.Scheme]: 1.0,
        [ScriptTheme.Female]: 1.1,
        [ScriptTheme.Occupation]: 1.0,
        [ScriptTheme.Comedy]: 1.1,
        [ScriptTheme.SeriousDrama]: 0.9,
        [ScriptTheme.Campus]: 1.1,
        [ScriptTheme.Music]: 1.0,
        [ScriptTheme.Performance]: 1.0,
        [ScriptTheme.Culture]: 0.7,
        [ScriptTheme.SciFi]: 1.2,
        [ScriptTheme.Disaster]: 1.0,
        [ScriptTheme.Military]: 0.9,
            } as Record<ScriptTheme, number>,

    /** 剧本池刷新周期（月） */
    SCRIPT_REFRESH_INTERVAL: 3,

    /**
     * 播出期长度（批6 定稿）：按剧本类型固定月数（游戏最小操作单位=月，⛔ 不足一月），
     * 播出期从杀青**次月**起算；名气发放月数 = 本值 + 1（杀青当月月末滴首月，见 ScriptSystem.payoutMonthsOf）。
     * ScriptDef.airMonths 可按剧覆盖（常规不填，override 须留痕，纪律同角色 reward 字段）。
     */
    AIR_MONTHS_BY_GENRE: { short: 1, movie: 1, web: 2, tv: 2, variety: 2, other: 1 } as Record<ScriptGenre, number>,
    /**
     * 普通 NPC 过气衰减比例（批6 占位 0.10）：剧本池刷新时判定，整季（SCRIPT_REFRESH_INTERVAL 个月）
     * 无拍摄/播出工作 → fame −= max(1, round(fame × 本值))，可掉咖位。
     * ⚠️ 最终数值待全部批次完成后由用户统一拍板，勿当作定稿值引用。
     */
    NPC_FAME_DECAY_RATE: 0.10,
    /**
     * **竞争型特殊 NPC 的名气收入倍率**（2026-10-24 数值定值＝×30，导入台账 §7.3 ④″；单人可覆盖：
     * `SpecialNpcDef.rival.fameIncomeMult`）。
     * 为什么要有它：NPC 侧滴灌是旧式 `heat × 番位系数`（一部戏 +35~50），而 10-22 门槛表已抬成等比 ×3.7
     * ⇒ 竞争型在新尺下数学上爬不动（Lv.5→6 差 2900，要拍 ~70 部），手段（阶段接戏门槛）就无从生效。
     * 为什么只乘竞争型、⛔ 不乘普通 NPC：全池抬水位会挤占玩家的当季角色位（Lv.6 角色更多 NPC 来抢），
     * 而群演的咖位玩家看不见；竞争型是玩家要「看着她熬上去」的对象，且阶段门槛把她的戏路压住了。
     * 定标（撤曲线后跑 18 年，属性 60）：×5 → 她反而掉到 Lv.4（被过气衰减吃光）；×20 → Y14 才升 Lv.6；
     * **×30 → Y7 升 Lv.6、Y14 后摸 Lv.7、终局 Lv.6~7**；×40 → 稳定 Lv.6~7。⇒ ×30 是「爬得急 + 有天花板」
     * 的最小可用档，且到不了 Lv.8/9（抢不走玩家的顶级位）。乔子珊按人设单独给 ×60（「起点高但滞涨」）。
     */
    NPC_FAME_INCOME_MULT: 30,

    /** 存档 key 前缀 */
    SAVE_KEY_MAIN: 'femaleStarSimulator_save_main',
    /**
     * 主存档副键（2026-10-13 A/B 双键轮换）：与主键交替写入，整盘 JSON 同步落盘中途被杀时，
     * 另一键仍是上一次写完的档。老档只存在主键里，读取时两键都试、取能解析且 timestamp 较新的一格，
     * 无需迁移、不升存档版本。
     */
    SAVE_KEY_MAIN_B: 'femaleStarSimulator_save_main_b',
    SAVE_KEY_META: 'femaleStarSimulator_save_meta',
    /**
     * 存档版本号
     * v16：合同拍摄日历化批（2026-10-27）——合同删 `remainingWeeks`（递减计数器）、改 `shotWeeksDone`
     *      （已拍周数），进度语义从「扣周」变「计数」，且「轧戏并行扣周」整段删除。
     *      旧档在途合同的 remainingWeeks 无法换算出等价的已拍量（旧模型下别的剧也在被并行扣周），
     *      ⇒ 一律拒载（用户 2026-10-27 拍板：「老档直接作废，只有我一个人玩」）。
     * v15：口碑可负批（2026-10-04）——`ATTRIBUTE_DEFS.reputation.min` 由 0 放开到 -100（运行时 ×ATTR_SCALE = **-500**），
     *      其余七维 min 仍 0。旧档的口碑读数全部产生于「扣到 0 就夹住」这条规则之下（事件 failEffects 的
     *      −2~−5 在旧档里是被夹掉后的值），放开下限后无法判断该补多少欠款，且商店/成就里「口碑 ≥ N」的
     *      历史达成也建立在旧下界上 ⇒ 一律拒载（⛔ 回填、⛔ 迁移）
     * v14：派角时序改革丙′（2026-10-01，docs/实现说明.md §3.4k + 迁存章 A1）——派角从季初 sweep 改为
     *      换新夜收口派角（sweepClosingScripts）、玩家 drip 门控/杀青追认/评选 in-flight 口径统一去 −1
     *      （签约月=收口月、制作窗=收口次月起）；在途 NPC job 的时钟锚与在途合同的档期语义均不可迁移
     *      （旧档按 旧公式 已滴灌的进度无法换算），一律拒载
     * v13：属性尺度扩充全案——门槛尺 ATTR_SCALE 3→5（上限 500、试镜门槛/事件 DC/咖位口碑线×5），
     *      成长侧新 GROWTH_SCALE=3 保持原绝对值；旧 v12 存档属性按 ×3 门槛尺存值，读进来
     *      面对 ×5 门槛全线偏低（=白送旧难度），一律拒载
     * v12：作品履历改带展示快照（FilmographyEntry 新增必填 workName/roleType/genre，播完结算时定格）——
     *      旧档履历只有坐标，剧本表改名即回写玩家历史，且无法补出快照，一律拒载
     * v11：微博剧宣批——粉丝改落盘字段（anchor=fame×K 月末收敛+扰动）、PostRecord 结构升级
     *      （likes/reposts 收益正比制落盘+剧宣挂 scriptId）、分类体系重排（日常并入营业、工作=剧宣限定、
     *      收益全线比例化），旧档缺 fans 且旧帖为月份伪公式不可补，一律拒载
     * v10：批6 滴灌时点修订——播出期从杀青次月起算、名气发放月数=播出期+1（杀青当月月末滴首月），
     *      旧 v9 在途 airing 合同倒计时语义错位（且总额已按旧节拍部分发放），一律拒载
     * v9：批6 作品生命周期（拍摄→播出→完结三段结算、filmography、NPC activeJobs/过气衰减），
     *     旧档合同/ NPC 缺在途字段且名气已按旧「杀青一把结」入账，一律拒载
     * v8：批4 片酬 9 档重铺+违约金规则改×2/Lv≤2免罚+休息价重定，旧档金钱读数失真、低于本版本一律拒载
     * v7：批3-D 咖位名气门槛表重排
     * v6：批3-A 属性尺度 ×3
     */
    SAVE_VERSION: 17,

    /**
     * 普通 NPC 开局生成数量
     * 奖项批实测（2026-09-26）：15 → 20 对奖项候选池**零提升**（固定排期下每部剧的角色位必然有人接，
     * 候选数只由排期里该片是否上架决定），只会多出整季无工可接、反复触发过气衰减的闲置 NPC ⇒ 已回退。
     */
    NORMAL_NPC_COUNT: 15,

    /**
     * 普通 NPC 中性别定额（2026-10-04 用户拍板「固定 8 男 7 女」）：生成时先按本值装男、余量装女，
     * 整袋洗牌后逐位领——⛔ 按顺序发，那会让高咖位段整块同一性别（旧写法没有这个问题是因为它根本随机）。
     * 为什么⛔ 再每人一次独立 50% 掷：奖项批实测 5 条 18 年周目里有一条摇出 **3 男 12 女**，
     * 于是影帝/男配两类别在该局无论排多少本都凑不满 4 个不同演员（同人限流之后名单钉死在 3 人、
     * 甚至 2 人），属⛔ 靠补剧本能救的结构死格（见 docs/新增功能-年度奖项与网友评价.md 批 H）。
     * 【调参旋钮】改本值 ⇒ 女性数 = `NORMAL_NPC_COUNT − 本值`；两性别各自的可参评位上限都跟着走。
     */
    NORMAL_NPC_MALE_COUNT: 8,

    /** 普通 NPC 初始咖位分布：[rank, count][]，总量 = NORMAL_NPC_COUNT */
    NORMAL_NPC_RANK_DISTRIBUTION: [
        [1, 5],
        [2, 4],
        [3, 3],
        [4, 2],
        [5, 1],
    ] as [number, number][],

    /** 剧本池每次刷新抽选的剧本数量 */
    MARKET_SCRIPT_COUNT: 6,

    /** 起始金钱 */
    START_MONEY: 10000,
    /** 起始体力 */
    START_ENERGY: 100,

    /** 带资进组解锁门槛：金钱**首次**达到该值才解锁（此后即使花钱低于也不回锁） */
    BUYIN_UNLOCK_MONEY: 200000,
    /** 带资进组解锁标记（player.flags key） */
    BUYIN_UNLOCK_FLAG: 'BUYIN_UNLOCKED',
    /** 富家千金开局金钱（代替默认 START_MONEY）；批4 随 BUYIN_UNLOCK_MONEY 同步 5万→20万，保住「开局即解锁带资」天赋语义 */
    HEIRESS_START_MONEY: 200000,

    // 造型工作室三常量（解锁咖位/标记/报价系数）已随自由出行批删除（2026-10-11 衣帽间化+商店买断制，
    // Lv.3 门与片酬×0.2 一口价双双作废）；旧档 flags 里的 STYLING_UNLOCKED 残留无害。
    /** 社交次数限制 */
    CHAT_LIMIT: 3,
    INVITE_LIMIT: 1,

    /** 通告偶遇概率（方案见 docs/玩法设计.md §八 + 实现说明迁存章 A3）：`P = BASE + (咖位-1) × STEP`，Lv.1=50% Lv.10=86% */
    ENCOUNTER_BASE_PCT: 50,
    ENCOUNTER_RANK_STEP_PCT: 4,
    /**
     * 熟遇「闲聊」那一格的好感增益。
     * 10-10 改版：旧「认真聊 +5 / 点头寒暄 +3」两档并成一档（低档是纯退路，没人点，实际手感恒为 +5）；
     * 定 +4 而非 +5，是为了让三格拉开成阶梯——免费闲聊 4 ＜ 邀约成功 5 ＜ 花库存送礼 6。
     * 与人脉页主动聊天（+2、全池 3 次/月）⛔ 共用文案池⛔ 共用额度（闸门是「每人每月一次偶遇」）。
     */
    ENCOUNTER_GAIN_CHAT: 4,
    /**
     * 熟遇「邀约」那一格：文案复用人脉页的 `inviteOkText`/`inviteFailText`，
     * 但**⛔ 走 `SocialSystem.invite`、⛔ 扣每月 1 次邀约额度**（用户 10-10 定：同池不同额度）。
     * 掷点口径与那条一致：成功率 = 好感/100，钳 90%。
     */
    ENCOUNTER_INVITE_GAIN_OK: 5,
    ENCOUNTER_INVITE_GAIN_FAIL: 1,
    ENCOUNTER_INVITE_PROB_CAP: 90,
    /** 咖位差「够不着」门槛（2026-10-12 用户令）：普通 NPC 咖位比玩家高 ≥ 此值时，初遇不搭理、暂时无法结识 */
    ENCOUNTER_UNREACHABLE_RANK_GAP: 3,

    // ===== 自由出行 · 周末阶段（2026-10-11 拍板，设计=docs/自由出行与小地图-设计方案.md §二/§三） =====
    /** 周末在家休息回体力 */
    WEEKEND_REST_ENERGY: 10,
    /** 周末出门行动点（1 点=一个目的地：商业街/影视城/公园三选一） */
    OUTING_ACTION_POINTS: 1,
    /** 探班好感增益（熟遇量级实装定档：对齐熟遇闲聊 +4 一档；不算熟遇、不受 encounterMetMonth 月戳） */
    VISIT_GAIN: 4,
    /** 探班防刷旋钮（2026-10-12 茄子令启用：「改成一个月探班同一个人最多一次」⇒ 钉 1；
     *  0 仍是「不启用」的形态，读 visitMonth 记账，将来调档只动这个数） */
    VISIT_MONTHLY_LIMIT: 1,
    /** 邀约赴约好感结算（赴约周末自动演出时落账；发起时的成功率判定不加好感——婉拒 +0，2026-10-11 拍板） */
    INVITE_WEEKEND_GAIN: 5,
    /** 咖啡馆兼职收入（2026-10-28 茄子令：进店可选「兼职」，一次 ¥600） */
    CAFE_JOB_PAY: 600,
    /** 咖啡馆「随便坐坐」掷中偶遇的概率（2026-10-28 茄子令：剩下=独坐档） */
    CAFE_LOUNGE_MEET_PCT: 50,
    /** 咖啡馆独坐回体力（2026-10-28 随「随便坐坐」档回归：一句独坐文案 +5 体力） */
    CAFE_SIT_ENERGY: 5,
    /** 公园掷中偶遇的概率（2026-10-28 茄子令：原「必遇 1 人」改 50%；剩下=散步休息档） */
    PARK_MEET_PCT: 50,
    /** 公园散步休息回体力（掷中休息档或池抽空兜底时 +5，与咖啡馆独坐同档） */
    PARK_REST_ENERGY: 5,

    /** NPC 主动打招呼（方案见 docs/玩法设计.md §八 + 实现说明迁存章 A3）：全局每月上限，超出按概率降序截断 */
    GREETING_MONTHLY_MAX: 2,
    /** 回应打招呼好感增益（不占每月聊天额度） */
    GREETING_REPLY_GAIN: 1,
    /** 咖位准入门槛：玩家咖位 → 该档 NPC 需达到的最低好感（低于门槛的 NPC 本月不可能打招呼） */
    GREETING_RANK_GATE: [
        { maxRank: 3, minFavor: 40 },   // Lv.1~3 熟悉
        { maxRank: 6, minFavor: 20 },   // Lv.4~6 认识
    ],
    GREETING_RANK_GATE_DEFAULT: 0,      // Lv.7+ 初识即可能打招呼
    /** 好感档基础打招呼概率（过门槛后；档位线 20/40/60/80） */
    GREETING_TIER_PROB: [
        { minFavor: 80, prob: 35 },
        { minFavor: 60, prob: 20 },
        { minFavor: 40, prob: 10 },
        { minFavor: 20, prob: 5 },
        { minFavor: 0, prob: 2 },
    ],
    /** Lv.7+ 对初识/认识（好感 <40）按咖位抬升打招呼概率（"上赶着巴结"） */
    GREETING_LOWTIER_BUMP: [
        { minRank: 7, prob: 5 },
        { minRank: 8, prob: 10 },
        { minRank: 9, prob: 15 },
        { minRank: 10, prob: 25 },
    ],
    /** 打招呼队列保留上限（防存档膨胀，超出裁剪最旧） */
    GREETING_QUEUE_MAX: 40,

    /** 粉丝营业：近N个月内须有发布记录 */
    POST_RECENT_MONTHS: 3,
    /** 每月发布条数上限（营业/工作剧宣/公益共享） */
    POST_MONTHLY_LIMIT: 3,

    // ===== 微博剧宣批（2026-09-26 设计定稿，方案=docs/微博剧宣与粉丝改造设计.md）=====
    // 用户全局原则：一切名气增减按已有名气比例，⛔ 定额（滴灌/事件/行程不在本批，另批重校）。
    // ⚠️ 2026-10-03 两度修订：① 纯比例＝复利 ⇒ 发帖类一律加「台阶封顶」；② 用户改判「名气主粮=作品」
    //   ⇒ 滴灌脱离 fame 比例、改按作品收益公式（2026-10-22 起为 FAME_INCOME_*，旧「台阶份额制」作废）
    //   ⇒ 「比例为主」现在只覆盖发帖与事件。
    /** 营业（普通微博）名气比例：max(1, round(fame×本值))；新人≈没人看是设计内 */
    POST_BIZ_FAME_RATE: 0.02,
    /**
     * 营业名气收益**封顶除数**（2026-10-03 名气溢出修·方案2 立 12；同日「主粮=作品」批重定为 360；**数值不动**）：
     *   每条收益 = max(1, min(round(fame×POST_BIZ_FAME_RATE), round(rankSystem.getIncomeBasis(咖位)÷本值)))
     * 为什么要有这道顶：比例制本身是复利（发帖读的是已被上一帖抬高的名气），⛔ 顶住就会一路指数冲出满级线。
     * 【2026-10-23 H 案批·注释重标】本值不改数，改的是口径：当年立 360 的理由是「实测占比落在 作品六至八成 /
     *   微博约两成」——那是**旧台阶制**的校准，咖位曲线批（10-22）与 H 案（10-23）把尺换掉后早已不成立。
     *   新尺实跑 18 年占比 = **作品 92.8% / 奖项 2.1% / 事件行程 2.6% / 微博 2.5%**（数值定标，脚本已清理）。
     *   乘子改读本级后封顶逐档只有 1~2 点/条（basis=30×乘子：Lv.4≈95、Lv.6≈346、Lv.7~10≈670~850，
     *   ÷360 后全档 round 成 1~2，外层 max(1,…) 兜底）；CAP_DIV 改到 600 也只是 ±1 点噪声，**两边都不如不动**——
     *   要真找回两成得砍到 5~10 量级＝复利后门重开，数值明令否决。语义接受：「名气主粮=作品」在新尺下
     *   更强，微博/公益名气＝点缀（公益无 max(1) 地板、低咖=0 是「做好事不留名」设计内）。
     *   早期一条只剩 1 点的读感代价（「粉丝几十万、转发上千、名气只加 1」）用户看过对照表后拍板接受。
     *   要重定 K 或改份额表，须先照上面口径重写一支一次性脚本（原 `_check_fame_mix.mjs` 已清理，git 可查）。
     */
    POST_BIZ_FAME_CAP_DIV: 360,
    /** 公益名气比例：⛔ 不设最低（fame<125 时=0，做好事不留名）；口碑是它的正薪 */
    CHARITY_FAME_RATE: 0.002,
    /**
     * 公益名气封顶除数（2026-10-03「主粮=作品」批补）：顶 = round(本级收入台阶 ÷ 本值)。
     * 2026-10-22 起与营业同源改读 `RankSystem.getIncomeBasis()`（不再读晋升台阶）。
     * ⛔ 给公益设这道顶，它就仍是纯比例抽成 ⇒ 名气越大越肥，Lv.8 起反超营业（Lv.10 一条 60 对 33），
     *   玩家改成「一个月三条全发公益」，复利从这条后门溜回来（0.2%×36 条/年 ≈ 年增 7%）。
     *   取营业的两倍除数＝封顶额度正好是营业的一半，与 0.2% 对 2% 的抽成比例同构 ⇒ 公益永不再是名气来源，
     *   它的溢价照旧在那条口碑腿上（`CHARITY_REP_GAIN`，2026-10-03 起 = 1 点）。低咖位段取整后为 0，与「做好事不留名」原语义一致（⛔ 兜底成一）。
     */
    CHARITY_FAME_CAP_DIV: 720,
    /**
     * 公益口碑定额（属性制不受比例化约束）。
     * 2 → 1（2026-10-03 用户拍板「公益改成发一条加一，别的不动」）：全公益玩家（每月刷满 3 条）年入账
     *   从 72 点腰斩到 36 点。⚠️ 实测（改后复跑，满档 4 部/年·每月三条全发公益）：18 年公益侧 648 点、
     *   剧完结侧 407 点 ⇒ 极端刷法下公益**仍占 61%**，第 10 年即把口碑顶到 500 上限。
     *   普通发法（公益 0~1 条/月）下作品侧稳占 94~96%，本批只治了这一档。
     * ⚠️ 移交口碑可负批：黑红线的「刷公益洗白」本值⛔ 治不住（上表第一行就是它），真正的闸门是
     *   「一年只有前 N 条计口碑」（同批讨论过的备选，年 6 点 ⇒ 18 年 108 点、作品侧回升到 86%）；
     *   用户本批选 ⛔ 加新字段。⚠️ 2026-10-07 黑红线已改口径（`ACH_198`＝Lv.7 且口碑 **< -100**，旧「≤200」作废）
     *   ⇒ 本条前置那句「读口碑绝对值」不再成立，年上限闸门仍挂在待办、等口碑数值大改一起做。
     */
    CHARITY_REP_GAIN: 1,
    /** 不营业扣罚：名气按 fame×本值（⛔ 旧定额 DEROOF_FAME_PENALTY 删除）、粉丝按 anchor×DEROOF_FANS_RATE */
    DEROOF_FAME_RATE: 0.01,
    /** 不营业脱粉比例（用户打回 15%→5%：掉的是路人盘，下月回补自然追一部分） */
    DEROOF_FANS_RATE: 0.05,
    /** 粉丝锚点系数：anchor = fame × 本值 × fanCharisma（Lv10 fame6000 → 千万级，量级方案1 单K线性） */
    FANS_ANCHOR_K: 2000,
    /**
     * 个体号召力系数区间（开局 roll 一次定档落盘）：让粉丝盘 ⛔ 等于「名气×2000」这条能被反推的整数台阶。
     * 单靠它只能把台阶挪成 2000×charisma（仍是台阶），消台阶还要下面的即时抖动。
     */
    FANS_CHARISMA_RANGE: [0.75, 1.35] as [number, number],
    /** 涨名气的即时抬粉比例（2026-09-28 用户定 50%）：当场补掉新增锚点缺口的一半，其余交月末收敛；定 100% 则粉丝退化成名气换算表 */
    FANS_IMMEDIATE_RATE: 0.5,
    /** 即时抬粉抖动 ±20%：每次涨粉的到账量都带零头（台阶真正由这项打碎） */
    FANS_IMMEDIATE_JITTER: 0.2,
    /** 月末粉丝朝锚点回补比例（均值回归主项：名气大涨月粉丝明显跟涨） */
    FANS_RECOVER_RATE: 0.25,
    /** 月末粉丝绕锚随机扰动（按锚点计⛔ 按现值计，防随机项长期漂移；随机只月末 roll 一次落盘，⛔ 读取端现算） */
    FANS_JITTER_RATE: 0.06,

    /**
     * 剧宣蹭剧权重（1~5 番）：不复用滴灌 FAME_BILLING_FACTORS——
     * 三张系数表（蹭剧/反哺三次方/后续实绩换算）语义独立，并排放此防后人图省事合并。
     */
    PROMO_BILLING_WEIGHTS: [1.0, 0.6, 0.35, 0.15, 0.05],
    /** 剧宣无番位（综艺嘉宾等）蹭剧权重 */
    PROMO_BILLING_WEIGHTS_NO_BILLING: 0.35,
    /** 剧贡献缩比：workHeat 池(16~76)×本值≈滴灌月薪一半，剧宣是甜点非主餐 */
    PROMO_DRAMA_K: 0.15,
    /**
     * 反哺比例：Δheat=fame×本值×W^(1/3)×min(1,锚/workHeat)。
     * 曾定 2% 被用户打回「天后三帖把小剧抬进爆剧区不合理——剧宣只能加成⛔ 决定性」→ 1% 并加装热度上限。
     */
    PROMO_FAME_TO_HEAT_Q: 0.01,
    /** Δheat 衰减锚（≈全库 heat P30=40）：剧越热个人号召力越不值钱（滚雪球自带刹车） */
    PROMO_DECAY_ANCHOR: 40,
    /** 甲案热度上限=底数×本值：小剧天花板锁死小剧区（h20→40/h50→100/h76→138），撞顶 Δheat 归零但 Δfame 照常 */
    PROMO_HEAT_CAP_MULT: 2,
    /**
     * 全网影视热度榜「爆」线（heatchart 批 2026-09-30 用户令「热度多少以上算爆，加个爆的标签」）：
     * workHeatOf ≥ 本值即渲染「爆」标。**爆标只跟当月显示热度同尺**——底数自带过线的剧本照样标爆
     * （2026-10-13 茄子令删掉「爆＝玩家砸出来的」这条口径；当日实测全库唯一命中者＝SCRIPT_901，heat 100＝设计内）。
     * 定线当年的实测分布（09-30 全库 144 本：底数 P50=50 / P90=62 / 最高 76）只作历史留档，
     * 不再构成「纯 NPC 剧永远摸不到线」的保证；调线只动这一个常量，它只改榜上标签语义，联动 workHeatOf 封顶结构＝无。
     */
    HEAT_BOOM_LINE: 100,
    /**
     * 热度榜长尾（2026-10-01 用户令「完播不掉榜，热度慢慢衰减」——在播剧太少、榜单寒酸）：
     * 作品播完（履历落账）后 HEAT_TAIL_MONTHS 个月内仍挂榜，热度 = 完播时热度 × DECAY^尾月（2026-10-01 用户拍板 0.5：首尾月 50%、次尾月 25%）
     * （逐月衰减下沉，自然形成「新上榜冲高→逐渐下沉→掉榜」的榜单生态；剧宣砸上去的热度
     * 也随长尾多挂数月，剧宣价值放大）。0 = 关闭长尾。
     */
    HEAT_TAIL_MONTHS: 2,
    HEAT_TAIL_DECAY: 0.5,

    /**
     * likes 粉丝盘比例制（2026-09-28 实机打回「粉丝一千多发条营业赞都有一千多」后重定）。
     * ⛔ 旧「收益正比制」(Δfame×1500+Δrep×2000) 整体作废：收益三项都跟粉丝盘无关
     * （Δfame 有 max(1,·) 兜底、剧宣蹭剧项只看剧热、公益只看口碑），小号一条帖的赞能大于粉丝总数。
     * 用户口径：「点赞量应该是粉丝量除以 100 左右」。读 fans 字段（非 fame）→ 涨粉滞后/脱粉掉赞都反映到数据上。
     */
    LIKES_FANS_RATE: 0.01,
    /** reposts 与名气同量级（用户口径「转发数量大概和名气差不多」）：fame × 本值 */
    REPOSTS_PER_FAME: 1,
    /**
     * likes/reposts 共用的分类系数：剧宣=1.0（差异化全交给下面的帖热因子，冷门剧剧宣不该比营业好看）；
     * 公益 0.8 略低于营业（它的溢价在口碑侧，⛔ 数据也更高→公益成性价比首选）。
     */
    POST_CATEGORY_COEFFS: { biz: 1.0, promo: 1.0, charity: 0.8 } as Record<'biz' | 'promo' | 'charity', number>,
    /** 剧宣帖热因子：1 + workHeat/本值（heat 底数 16~76→×1.16~1.76；宣传顶到上限 138→×2.38）。⛔ 番位不参与（用户拍板） */
    PROMO_POST_HEAT_DIV: 100,
    /**
     * likes 抖动 ±30%、reposts 抖动 ±40%（2026-09-28 二修：旧 ±10%/±25% 被用户判「数字太乖」——
     * 连发十条看不出哪条冷哪条热）。⛔ 拟真长尾（用户判「赞和转只是数字，⛔ 太拟真」），
     * 只用加宽均匀带出起伏；要「偶发爆帖」需改单帖命运动数结构，已明确不做。
     */
    LIKES_JITTER: 0.3,
    REPOSTS_JITTER: 0.4,

    // ===== 好友客串批②·方向二（设计=docs/好友客串设计.md §一，数值=用户 2026-09-26/28 定档，⛔ 擅改）=====
    /** 请好友出山的交情门槛（好友档；与被请侧 §二 齐平） */
    GUEST_MIN_FAVOR: 60,
    /** Δheat = max(1, round(好友fame × 本值 × min(1, GUEST_DECAY_ANCHOR/自己剧workHeat))) */
    GUEST_HEAT_K: 0.003,
    /** 热度衰减锚（与剧宣 PROMO_DECAY_ANCHOR 同值但口径独立，改动须分别过用户） */
    GUEST_DECAY_ANCHOR: 40,
    /** 客串/好友剧宣**软顶**（2026-10-28 B+C 案茄子拍板）：两通道共池 Σ 的天花板=底数×本值。
     *  ⛔ 撞顶锁邀请（旧硬顶语义已废）——收益随池满线性衰减（fade=1−Σ/cap），触顶后收益 0、请求仍成立（好感照扣）。
     *  1→3：抬高上限给软顶留出可用空间（旧 ×1 会被一次高咖位客串直接打满=锁死）。 */
    GUEST_HEAT_CAP_MULT: 3,
    /** 好感消耗阶梯（按好友 Lv 取，1-based；= 巨星人情 ≈ 两个多月专供聊天的好感收入） */
    GUEST_FAVOR_COSTS: [10, 10, 10, 10, 20, 20, 20, 30, 30, 30],
    /** 好友沾光：fame += round(自己剧 workHeat × 本值)（≈接一次边角位量级；⛔ 上屏，用户口令） */
    GUEST_NPC_FAME_K: 0.1,

    // ===== 好友客串批③·方向一（好友邀玩家客串，设计=docs/好友客串设计.md §二）=====
    /** 月初有合格候选（好感≥60 且在拍）时全局掷一次的本值%概率出邀请，命中抽 1 人（≈一年两三次封顶，用户定档） */
    GUEST_INVITE_PCT: 20,
    /** 接受蹭名气：fame += max(1, round(好友剧底数 heat × 本值))（NPC 剧无 workHeat 概念，取静态底数） */
    GUEST_RIDE_K: 0.1,
    /** 接受邀请的好感奖励（好友档人情，⛔ 婉拒不罚=0） */
    GUEST_RIDE_FAVOR: 3,

    // ===== 好友剧宣批（2026-10-28：一番/二番开播后请好友站台，现实对标=好友开播时发条宣传微博）=====
    // 定位=客串的小头（茄子 2026-10-28 拍板）：「客串是来演戏，宣传是发条微博」⇒ 好感消耗与热度系数都取客串一半；
    // ⛔ 好友不沾光涨名气（同批拍板，区别于客串 settle）；⛔ 无拒绝无反噬（好感≥60 已是朋友，确定性兑现——
    // 「被扒」的不确定性留给经纪人商务通稿，私交≠商务）。封顶与客串**共用** GUEST_HEAT_CAP_MULT 池。
    /** Δheat = max(1, round(好友fame × 本值 × min(1, GUEST_DECAY_ANCHOR/剧workHeat)))（= GUEST_HEAT_K 的一半） */
    FRIEND_PROMO_HEAT_K: 0.0015,
    /** 好感消耗阶梯（按好友 Lv 取，= GUEST_FAVOR_COSTS 减半） */
    FRIEND_PROMO_FAVOR_COSTS: [5, 5, 5, 5, 10, 10, 10, 15, 15, 15],

    // ===== 年度奖项批（设计=docs/新增功能-年度奖项与网友评价.md）=====
    // 名气收益取**定额**（用户 2026-09-26 拍板）：与滴灌/主线事件同属「事件侧名气发放」口径，
    // 全局比例化原则至今只覆盖微博批，奖项不例外于未重校的其余系统。
    /**
     * 获奖名气定额。换算基准随尺漂移，这里挂**全库同步点清单第⑦条**：奖项/事件/行程的定额是写死的绝对值、
     *   **不吃 `FAME_INCOME_K`** ⇒ K 280→110 后其相对权重自动上涨。10-23 H 案定标读数（本尺）：Lv.3 一部戏
     *   ≈103 点 ⇒ 一座奖（150）≈ 1.5 部戏（旧尺是 0.3 部）；Lv.6 一部 ≈730 ⇒ 奖退化成零头。即「拿奖比拍戏快」
     *   只在 Lv.3~4 的低咖段边际成立（150 = 本级台阶 220 的 68%，一拍跨大半级）。
     *   18 年总占比仍 作品 92.8% / 奖项 2.1% ⇒「主粮=作品」不破，**定额组本批不缩**（用户拍板）。
     *   若 QA 带获奖节奏的跑线实测出「跨级靠拿奖」的观感，收的档位是 WIN 150→110 / NOM 40→30（奖=1 部戏），
     *   另批动、不并进 H 案，以免定标归因不清。旧基准「≈3 部高热一番」是 2026-09-26 台阶制读数，已作废。
     */
    AWARD_FAME_WIN: 150,
    /** 提名名气定额（旧基准「≈一部中等一番剧」同为台阶制读数，作废；本尺见上一条的换算注） */
    AWARD_FAME_NOM: 40,
    /** 获奖口碑定额（属性制不受比例化约束；1.25 部满分剧的量，不构成 Lv.8+ 晋升捷径） */
    AWARD_REP_WIN: 10,
    /** 提名口碑定额 */
    AWARD_REP_NOM: 3,
    // ⚠️ 2026-10-21 用户令：同一奖同一类别**重复获奖不再衰减**（原 `AWARD_REPEAT_FACTOR = 0.5`）——
    //   名气与口碑都按上面的定额给满；该常量与两处用法（`AwardSystem.payOut` / `payoutOfCeremony`）一并退役。
    /** 评选总分的热度除数：热度项 = min(1, workHeat / AWARD_HEAT_DIV)，⛔ 无系数（用户 09-28 定：去掉原 ×1.5） */
    AWARD_HEAT_DIV: 100,
    /**
     * 评审随机 ±本值（保留爆冷与「该拿没拿」；随机只在提名时 roll 一次并冻结，颁奖夜不重掷）。
     * ⛔ 咖位项已于 2026-09-28 删除（用户改定）：总分 = 表演分 + 热度项 + 随机，咖位不进评选。
     * 09-28 同日 0.8→0.3：删咖位项后 1st−2nd 差距中位仅 0.35 分（18 年定标实测），
     * 随机若留 0.8 ⇒ 33/46 个类别的得主实际由随机决定（=抽奖）。0.3 ≈ 差距中位，爆冷仍可能发生但⛔ 主导。
     */
    AWARD_JITTER: 0.3,
    /**
     * 提名名额：min(候选数, min(AWARD_NOMINEE_CAP, max(AWARD_NOMINEE_FLOOR, ceil(候选数 × AWARD_NOMINEE_RATIO))))
     * 09-28 用户改定：比例 0.5→0.67、名额由固定 5 改浮动（薄池届⛔ 齐刷 5 人，看着像模板）。
     * 09-30 用户改定：**4~6 → 4~5**（CAP 6→5，配套同人限流+参评线两把锁，见 AwardSystem.nomineeLists）。
     * min(候选数, …) 是地板：候选比下限还少时按实有人数给，⛔ 凭空造提名（薄池届宁可少提名/空缺）。
     */
    AWARD_NOMINEE_CAP: 5,
    AWARD_NOMINEE_FLOOR: 4,
    AWARD_NOMINEE_RATIO: 0.67,
    /**
     * 参评评分线（09-30 茄子拍板；10-04 用户拍板 7.0 降为 **6.5**，即「甲」；10-15 茄子拍板再降为 **6.0**）：
     * 实际评分 score ≥ 本线才有参评资格，低于线的作品连候选池都进不了。
     * 评分通胀修（§3.1b）之前全挤 10 分时此线无意义，改版后恢复区分度才立得住。
     * 口径：NPC score=baseScore（剧本质量线）；玩家=实际评分（演技可把 7 分本拉过线，烂本演到顶也过不了）——
     * 玩家保有主动权，烂本冲奖之路关死（与《热钱》对赌本设计呼应）。
     * ⚠️ 这颗旋钮治不了「名单只有 3 人」：18 年×3 真跑（脚本已清理，git 可查）显示降线只把每届每类别
     * 过线作品从 5 部抬到 6 部，而**这 6 部只由 3 个 NPC 演过** ⇒ 同人限流之后名单仍钉在 3 人。
     * 同一张表里另两条也被否：按该类别分数分布自动划线的「活线」（读数一格不动，且多一颗常量，已回退）、
     * 扩 NPC 池（履历总量钉死 537＝供给由排期决定，加人只加闲置与过气衰减，见 `NORMAL_NPC_COUNT`）。
     * 用户同日裁定：同人限流 `AWARD_PERFORMER_NOMINEE_CAP` 保持 1「不能改」⇒
     * 名单厚度的真闸在**排期供给**（够格的高分本太少，补位路线见 docs/奖项补位-角色缺口.md），⛔ 再来动这四道规则。
     * 【调参旋钮】觉得提名太稀就往下调（现值 6.0）；觉得含金量不足就往上调（7.0）。
     */
    AWARD_NOMINEE_MIN_SCORE: 6.0,
    /**
     * 同类别同人提名限部（09-30 茄子拍板 A 案）：每类别每个演员只入围其 total 最高的达标作品。
     * 旧口径按「作品位」提名（去重键 scriptId#roleIndex），头部 NPC 多部同入围一人包场（实测）。
     * 【调参旋钮】想放开「一人双提」改 2（不推荐，薄 NPC 池下照样包场）。
     * ⚠️ 10-04 实测过这条旋钮并**由用户裁定作废**：改 2 确实把名单中位从 3 抬到 4（打满 5 人的格子 1 → 16），
     * 但「名单内不同演员数」仍是 3 —— 多出来的那格是同一个人的第二部戏，买到的只有长度没有多样性
     * （且得主恒取 total 首位，一人第二部永远压不过他自己的第一部 ⇒ 谁获奖⛔ 变，纯观感）。
     * 用户原话「乙那个同人限流不能改」（2026-10-04）⇒ 本值钉 1，名单厚度的问题归排期供给（见上一条注释）。
     */
    AWARD_PERFORMER_NOMINEE_CAP: 1,

    /** 行程执行间隔（毫秒），为玩家留有阅读日志的时间 */
    WEEK_EXECUTE_DELAY: 800,

    /** 随机事件每周触发概率（0-1） */
    RANDOM_EVENT_PROBABILITY: 0.3,

    /** 小助理队列上限（超出裁剪最旧） */
    ASSISTANT_QUEUE_MAX: 50,

    // ===== 开局特性点数制（2026-10-21 机制批；数值档待用户后续拍板，先占位）=====
    /**
     * 开局特性点数预算（基础值，不含成就商店买的额外点数）。
     * 玩家在建角页自由点选特性：正面特性花点数、负面特性回点数（纯负效果，见 `TagDef.traitPoints`）。
     * 【调参旋钮】改这一个数＝改「开局能买几个特性」。
     */
    TRAIT_POINTS_BASE: 3,
    /**
     * 负面特性最多能选几张 —— **防退化的硬闸**：
     * 负面特性回点数，若不设上限，玩家会「先吃满负面回点、再把点数全换成强正面」＝白嫖。
     * 上限 1 ⇒ 满额正面＝2 张 2 点特性 + 1 张代价，决策成立且有天花板。
     */
    TRAIT_NEGATIVE_MAX: 1,
    /** 至少要选几项特性才能进下一步（0＝允许空手上路；1＝与旧机制「开局保底 1 张天赋」对齐） */
    TRAIT_MIN_PICKED: 1,
    /**
     * 成就商店「额外特性点」最多能买到几点。
     * ⚠️ 商店购买存在 **meta 存档里（跨周目累积）** ⇒ 不封顶就是逐周目永久变强，这条必须封死。
     */
    TRAIT_POINTS_SHOP_MAX: 2,

    /**
     * **口碑分档边界**（评价面五档的唯一口径源；2026-10-22 从 `ReviewSystem.repBand()` 抽出来，**值不变**）：
     * `T1 ≤ -301／T2 -300~-101／T3 -100~100／T4 101~300／T5 301~500`（运行时值）。
     * 评价面与随机黑帖（按口碑分档的**池内权重**／评论差分）**共用这一份边界**，别各写一套。
     */
    REP_BAND_EDGES: [-301, -101, 100, 300] as const,

    // ===== 数值事件（2026-10-22 统一进事件表）=====
    /**
     * **数值事件的月度总闸：按咖位给「这个月有没有事」的概率**（用户 2026-10-22 定档；2026-11-01 令 Lv.8~10 提到 60%；
     * 2026-11-03 令 Lv.4~8 改为每档 +10%：10/20/30/40/50%，Lv.8~10 同为 50% 封顶）。
     * 下标＝咖位-1（Lv.1~10）；Lv.1~3 给 0 ＝ 还没人盯你，数值事件压根不演。
     *
     * 两道闸的口径（别再退回「每条事件各自掷概率」那套）：
     *   ① **总闸**＝本表按咖位取概率，掷中才有事（每月至多一条，结构上就只抽一条）；
     *   ② **池内抽取**＝在门槛满足的事件里按 `trigger.weight` 轮盘抽（权重可写按口碑分档的表，
     *      口碑越差权重越高；0 权重＝这一档不抽，用 `trigger.require` 挡更直白）。
     * 触发时点＝每月执行完行程之后的决策页，不许晚于每月的影视热度榜。
     */
    NUMERIC_EVENT_RANK_CHANCE: [0, 0, 0, 0.10, 0.20, 0.30, 0.40, 0.50, 0.50, 0.50] as number[],

    /**
     * 数值事件的话题度按**名气**换算：`buzz = clamp(round(BASE × 名气^POWER), 1, MAX)`。
     * 2026-10-22 立（用户令「名气越高话题度越高、最高 300」）；2026-10-23 咖位曲线批重定标：
     * 名气量纲整体 ×3.7（顶流 2200→15000），原 BASE/POWER 会在 Lv.6 就顶满 ⇒ 现改
     * BASE 0.982 / POWER 0.595，**封顶点落在顶流 15000**。读数：30→7、80→13、300→29、
     * 1100→63、4000→137、15000→300（Lv.10 仍 300，⛔ 超过 MAX）。
     */
    NUMERIC_EVENT_BUZZ_FROM_FAME: { BASE: 0.6495, POWER: 0.595, MAX: 300 },

    // ===== 免试镜直签（2026-10-25 新手引导批，docs/新手引导-设计方案.md §二）=====
    /**
     * **帮谈月掷率**（Lv.1~4，经纪人磨机会）：每月月初掷一次，掷中生成一条三四番直签戏约
     * （均值的 12% ≈ 每年 1~2 单）。Lv.1 也参与月掷（2026-10-27 茄子令；开局首单仍由
     * `DirectSignSystem.issueFirstOffer` confirm 后必发，两者独立）。
     */
    DIRECT_SIGN_BROKER_CHANCE: 0.12,
    /**
     * **递本月掷阶梯**（Lv.5+，片方指名邀约）：下标＝咖位-5。
     * Lv.5=15% → 6=20% → 7=26% → 8=34% → 9=42% → 10=50%（2026-10-27 茄子令整体上调，曲线保持递增）。
     * 掷中给**一二番**（番位优先＝「不用抢」的特权感；一二番无货当月不发，⛔ 兜底给配角）。
     * 挑角咖位尺按咖位分段（见 DirectSignSystem.pickCandidate）：Lv.5~6 收本档/低一档；Lv.7+ 只收低一/两档。
     * 数组形态先例＝`NUMERIC_EVENT_RANK_CHANCE`（同数值事件的旋钮表）。
     */
    DIRECT_SIGN_PITCH_LADDER: [0.15, 0.20, 0.26, 0.34, 0.42, 0.50] as number[],
} as const;
